<script setup lang="ts">
import { onMounted, ref } from "vue";
import type { DRDrawing, DRPartialProject } from "@/types";

import { drawingRepository } from "@/repositories/drawing.repository";
import { projectRepository } from "@/repositories/project.repository";

import CanvasDraggableItems from "@/components/canvas/layouts/CanvasDraggableItems.vue";

import { Logger } from "@/logger";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { resetObservationsPanel } from "@/stores/ObservationsPanelState";
import { resetCatalogueTabs } from "@/stores/CatalogueTabsState";

import CanvasHeader from "./layouts/CanvasHeader.vue";
import CanvasDrawersMenus from "./layouts/CanvasDrawersMenus.vue";
import CanvasToolbar from "./layouts/CanvasToolbar.vue";
import CanvasToolbarOptions from "./layouts/CanvasToolbarOptions.vue";
import {
  isFullscreen as fullscreen,
  setFullscreen as setFullscreenState,
  isCanvasDrawing,
} from "@/stores/CanvasViewState";
import PhotoDraggable from "./Tools/PhotoDraggable.vue";
import MemoryAlertBanner from "./MemoryAlertBanner.vue";
import DynBlockValuePicker from "./DynBlockValuePicker.vue";
import ToolHintBar from "./ToolHintBar.vue";
import ImageInsertingIndicator from "./ImageInsertingIndicator.vue";
import CanvasDisplayDialog from "./display/CanvasDisplayDialog.vue";
import UiChangesDialog from "./uiChanges/UiChangesDialog.vue";
import { editingLocked } from "@/stores/EditingLockState";

const props = defineProps<{
  drawingId: number;
}>();

const setFullscreen = (value: boolean) => {
  Logger.info(`CanvasLayout.vue : toggleFullscreen`);
  setFullscreenState(value);
};

const file = ref<DRDrawing | undefined>();
const project = ref<DRPartialProject | undefined>();

const init = async () => {
  // Chaque plan s'ouvre sur le premier catalogue, ligne des notes dépliée.
  resetCatalogueTabs();
  file.value = await drawingRepository.getById(props.drawingId);
  if (file.value?.projectId) {
    project.value = await projectRepository.getById(file.value.projectId);
  }
  // Recharge l'analyse du plan persistée (onglet IA + cadres) ; le volet
  // Observations repart de son état initial à chaque plan (onglet, filtre,
  // tri, cadres).
  resetObservationsPanel();
  await usePlanAnalysisState().loadForDrawing(props.drawingId);
};

onMounted(async () => {
  await init();
});
</script>

<template>
  <CanvasHeader
    :drawing-id="drawingId"
    :fullscreen="fullscreen"
    @update:fullscreen="setFullscreen($event)"
    :file="file"
    :project="project"
  ></CanvasHeader>

  <!-- Fusion de versions en cours : outils d'édition grisés (la navigation
       reste libre, cf. EditingLockState) -->
  <CanvasToolbar :class="{ 'editing-locked': editingLocked }" />

  <CanvasToolbarOptions
    :fullscreen="fullscreen"
    :class="{ 'editing-locked': editingLocked }"
  />

  <CanvasDraggableItems :class="{ 'editing-locked': editingLocked }" />

  <PhotoDraggable
    :fullscreen="fullscreen"
    :class="{
      'drawing-active': isCanvasDrawing,
      'editing-locked': editingLocked,
    }"
  />

  <CanvasDrawersMenus :drawing-id="drawingId" :fullscreen="fullscreen" />

  <!-- Opened by the dynamic block list handles on the canvas -->
  <DynBlockValuePicker />

  <ToolHintBar />

  <!-- Photo / image en cours d'ajout sur le plan -->
  <ImageInsertingIndicator />

  <MemoryAlertBanner :fullscreen="fullscreen" />

  <!-- Ouverte depuis le menu fichier ou les réglages -->
  <CanvasDisplayDialog />

  <!-- « L'interface évolue » : s'ouvre d'elle-même la 1re fois sur l'appareil -->
  <UiChangesDialog />
</template>

<style lang="scss">
// Fusion de versions en cours : outils visibles mais inactifs. Les descendants
// aussi : certains (curseurs, menus d'options) redéfinissent pointer-events.
.editing-locked {
  opacity: 0.4;

  &,
  * {
    pointer-events: none !important;
  }
}
</style>
